import { Button } from "@/components/ui/je-button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Mail, MapPin, Linkedin, Instagram, ArrowRight } from "lucide-react"
import { useServiceSelection } from "@/components/ServiceSelectionProvider"
import { ServiceChip } from "@/components/ui/service-chip"
import { useState, useEffect } from "react"
import { useLanguage } from "@/contexts/LanguageContext"
import { useBotProtection } from "@/hooks/use-bot-protection"
import { useToast } from "@/hooks/use-toast"
import { Honeypot } from "@/components/ui/honeypot"

const ContactSection = () => {
  const { selectedServices, removeService, getMessageText, getServicesCSV } = useServiceSelection()
  const { t } = useLanguage()
  const [messageValue, setMessageValue] = useState("")
  const { toast } = useToast()
  const { isSubmitting, handleHoneypotChange, submitForm, honeypotValue, enableHoneypot } = useBotProtection()

  useEffect(() => { setMessageValue(getMessageText()) }, [selectedServices, getMessageText])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const data = {
      type: 'contact' as const,
      name: fd.get('name') as string,
      prenom: fd.get('prenom') as string,
      email: fd.get('email') as string,
      telephone: fd.get('telephone') as string,
      organization: fd.get('organization') as string,
      budget: fd.get('budget') as string,
      message: fd.get('message') as string,
      services_selected: fd.get('services_selected') as string,
    }
    const r = await submitForm(data)
    if (r.success) {
      toast({ title: "Succès !", description: r.message })
      e.currentTarget.reset()
      setMessageValue(getMessageText())
    } else {
      toast({ title: "Erreur", description: r.error, variant: "destructive" })
    }
  }

  return (
    <section id="contact" className="py-24 md:py-32 bg-gradient-dark text-je-white relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-[0.06]" aria-hidden="true" />
      <div className="container-editorial relative">
        <div className="grid grid-cols-12 gap-8 mb-16 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <span className="eyebrow mb-5" style={{ color: 'hsl(var(--je-red))' }}>07 — Contact</span>
            <h2 className="text-section-title font-serif font-medium mt-4 text-balance">{t('contact.title')}</h2>
            <p className="mt-6 text-xl text-je-white/75 max-w-xl text-pretty">{t('contact.subtitle')}</p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-12">
          {/* Form */}
          <div className="col-span-12 lg:col-span-7 bg-je-white text-je-black p-8 md:p-12 rounded-md">
            <form className="space-y-6 form-modern" onSubmit={handleSubmit}>
              {enableHoneypot && <Honeypot value={honeypotValue} onChange={handleHoneypotChange} />}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="input-group">
                  <Input id="name" name="name" placeholder=" " required className="h-12" />
                  <Label htmlFor="name">{t('contact.name')} *</Label>
                </div>
                <div className="input-group">
                  <Input id="prenom" name="prenom" placeholder=" " required className="h-12" />
                  <Label htmlFor="prenom">{t('contact.firstname')} *</Label>
                </div>
              </div>

              <div className="input-group">
                <Input id="email" name="email" type="email" placeholder=" " required className="h-12" />
                <Label htmlFor="email">{t('contact.email')} *</Label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="input-group">
                  <Input id="telephone" name="telephone" placeholder=" " className="h-12" />
                  <Label htmlFor="telephone">{t('contact.phone')}</Label>
                </div>
                <div className="input-group">
                  <Input id="organization" name="organization" placeholder=" " className="h-12" />
                  <Label htmlFor="organization">{t('contact.organization')}</Label>
                </div>
              </div>

              {selectedServices.length > 0 && (
                <div>
                  <Label className="mb-2 block text-sm font-medium text-je-navy">{t('contact.services.selected')}</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedServices.map((s) => (
                      <ServiceChip key={s.id} service={s.name} onRemove={() => removeService(s.id)} />
                    ))}
                  </div>
                </div>
              )}

              <div className="input-group">
                <Textarea id="message" name="message" rows={5} placeholder=" " required
                  value={messageValue} onChange={(e) => setMessageValue(e.target.value)} />
                <Label htmlFor="message">{t('contact.message')} *</Label>
              </div>

              <input type="hidden" name="services_selected" value={getServicesCSV()} />

              <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                <span className="gap-2 inline-flex items-center">
                  {isSubmitting ? "Envoi en cours..." : t('contact.send')}
                  <ArrowRight className="w-4 h-4" />
                </span>
              </Button>
            </form>
          </div>

          {/* Info */}
          <div className="col-span-12 lg:col-span-5 space-y-10">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-je-red mb-4">{t('contact.info.email')}</div>
              <a href="mailto:je.hepia@hesge.ch" className="font-serif text-2xl md:text-3xl text-je-white hover:text-je-red transition-colors inline-flex items-center gap-2 group">
                je.hepia@hesge.ch
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>

            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-je-red mb-4">{t('contact.info.address')}</div>
              <p className="font-serif text-xl text-je-white leading-relaxed">
                Rue de la Prairie 4<br />1202 Genève · Suisse
              </p>
            </div>

            <div className="hairline" />

            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-je-red mb-4">{t('contact.social')}</div>
              <div className="flex gap-3">
                <a href="https://www.linkedin.com/company/jehepia/" target="_blank" rel="noopener noreferrer"
                  className="w-11 h-11 border border-je-white/20 hover:border-je-red hover:bg-je-red flex items-center justify-center transition-all rounded-sm">
                  <Linkedin className="w-4 h-4" />
                </a>
                <a href="https://www.instagram.com/jehepia/?hl=fr" target="_blank" rel="noopener noreferrer"
                  className="w-11 h-11 border border-je-white/20 hover:border-je-red hover:bg-je-red flex items-center justify-center transition-all rounded-sm">
                  <Instagram className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="border border-je-white/15 p-6 rounded-md">
              <div className="font-mono text-xs uppercase tracking-widest text-je-red mb-3">{t('contact.student.title')}</div>
              <p className="text-je-white/80 mb-5 text-sm leading-relaxed">{t('contact.student.subtitle')}</p>
              <a href="/rejoindre" className="inline-flex items-center gap-2 text-je-white border-b border-je-white/30 hover:border-je-red hover:text-je-red transition-colors pb-1 text-sm">
                {t('contact.student.button')} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
export default ContactSection
